<template>
  <div :id="'event-' + event.id" class="event-container" :data-event-id="event.id">
    <div class="event-header d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center">
        <user-short class="actor-avatar align-self-baseline" :user-id="event.actorId" length="40" />
        <div class="ml-2">
          <span class="bold">{{ getDisplayName(event.actorId) }}</span>
          <span class="px-1" v-if="event.type === IDEA_EVENT_TYPE.COMMENT">added a comment</span>
          <span class="px-1" v-else-if="event.type === IDEA_EVENT_TYPE.CREATED"
            >created the Idea</span
          >
          <span class="event-time text-small">{{ localizedDateString(event.createdAt) }}</span>
        </div>
      </div>
    </div>
    <template v-if="event.type === IDEA_EVENT_TYPE.COMMENT">
      <div class="event-content event-comment-container col-12 pr-1" v-if="!editable">
        <height-limiter label="Show full comment">
          <div class="event-comment" v-html="comment"></div>
        </height-limiter>
        <div
          class="comment-edit-menu row no-gutters position-absolute"
          v-if="user.id === event.actorId"
        >
          <b-button variant="link" class="text-prime p-0 mr-2" @click="switchToEditMode"
            >Edit
          </b-button>
          <b-button
            variant="link"
            class="text-prime p-0 mr-2"
            v-b-modal="'confirm-modal-' + event.id"
            >Delete
          </b-button>
        </div>
      </div>
      <template v-else>
        <div class="edit-comment" :class="{ 'errored-field': errors && errors.length }">
          <ck-editor
            v-if="mentionableUsers.length > 0"
            theme="dark"
            :ref="'eventCommentCkEditor' + event.id"
            :config="ckeditorConfig"
            :id="'eventCommentEditor' + event.id"
            :initial-data="event.comment"
            :initial-data-type="event.commentType"
            @save="editComment"
          />
          <div class="d-flex">
            <submit-buttons
              class="mt-3"
              submit-text="Save"
              @save="editComment($refs['eventCommentCkEditor' + event.id].getData())"
              @cancel="removeCommentEditState(event)"
            />
          </div>
        </div>
        <div class="field-error-message" v-if="errors && errors.length > 0">
          {{ errors.join('. ') }}
        </div>
      </template>
      <confirm-dialog
        :id="'confirm-modal-' + event.id"
        @ok="deleteComment"
        ok-title="Yes, Delete"
        :hide-header="true"
        ok-variant="danger"
      >
        Please confirm that you want to delete the comment.
      </confirm-dialog>
    </template>
  </div>
</template>

<script>
import { IDEA_EVENT_TYPE } from '@/constants/idea'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import { BButton, VBModal } from 'bootstrap-vue'
import UserShort from '@/components/UserShort'
import SubmitButtons from '@/components/SubmitButtons'
import CkEditor from '@/components/CkEditor'
import ConfirmDialog from '@/components/ConfirmDialog'
import HeightLimiter from '@/components/HeightLimiter'
import { localizedDateTimeString } from '@/datetime'
import URLify from '@/utils/URLify'
import editManager from '@/editManager'
import { DEFAULT_ERROR } from '@/constants/messages'
import EventBus from '@/EventBus'
import { TOAST_TYPE } from '@/constants/toasts'
import { getCKEditorConfig } from '@/ckeditorConfig'
import { MIME_TYPE } from '@/constants/common'
import formatFreeText from '@/utils/formatFreeText'
import { formatDate } from '@/utils/formatDate'

/**
 * This component renders idea events - created, commented
 */
export default {
  name: 'IdeaEvent',
  components: {
    BButton,
    UserShort,
    ConfirmDialog,
    CkEditor,
    SubmitButtons,
    HeightLimiter
  },
  directives: {
    'b-modal': VBModal
  },
  props: {
    event: {},
    onSave: {},
    /*
    Users that can be mentioned while editing comment
     */
    mentionableUsers: Array,
    idea: Object
  },
  data() {
    return {
      editable: false,
      showDeleteConfirmation: false,
      errors: [],
      IDEA_EVENT_TYPE
    }
  },
  computed: {
    isCommentEvent() {
      return this.event.type === IDEA_EVENT_TYPE.COMMENT
    },
    user() {
      return this.$store.getters.user
    },
    comment() {
      return formatFreeText(this.event.organizationId, this.event.comment, this.event.commentType)
    },
    ckeditorConfig() {
      return getCKEditorConfig({
        height: 200,
        startupFocus: true,
        users: this.mentionableUsers,
        orgId: this.event.organizationId
      })
    }
  },
  methods: {
    ...mapActions('idea', ['updateIdeaEvent', 'deleteIdeaEvent']),
    URLify,
    removeCommentEditState() {
      this.editable = false
    },
    localizedDateString(date) {
      if (!date) return ''
      if (typeof date === 'string') {
        date = new Date(Date.parse(date))
      }
      return localizedDateTimeString(date)
    },
    switchToEditMode() {
      this.editable = true
    },
    async editComment(comment) {
      try {
        const event = await this.updateIdeaEvent({
          id: this.idea.id,
          comment,
          commentType: MIME_TYPE.HTML,
          orgId: this.event.organizationId,
          actorId: this.user.id,
          eventId: this.event.id
        })
        this.editable = false
        this.$emit('update', event)
        EventBus.$emit('ADD_TOAST', { message: 'Comment has been edited successfully' })
      } catch (e) {
        this.errors = R.pathOr([DEFAULT_ERROR], ['response', 'data', 'detail', 'comment'], e)
      }
    },
    async deleteComment() {
      try {
        await this.deleteIdeaEvent({
          id: this.event.ideaId,
          orgId: this.event.organizationId,
          actorId: this.user.id,
          eventId: this.event.id
        })
        EventBus.$emit('ADD_TOAST', { message: 'Comment has been deleted successfully' })
        this.$emit('delete', this.event)
      } catch (e) {
        const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
      }
    },
    getDisplayName(userId) {
      return this.$store.getters.userById(userId)?.displayName
    },
    formatDate
  },
  watch: {
    editable(editable) {
      if (editable) {
        this.editId = editManager.enterEditMode()
      } else {
        editManager.exitEditMode(this.editId)
      }
    }
  },
  beforeDestroy() {
    this.editId && editManager.exitEditMode(this.editId)
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/errors';

.event-container {
  font-size: 0.9375rem;
  border-bottom: 1px solid $borderSecondary;
  margin-top: 8px;
  padding-bottom: 8px;
}

.comment-edit-menu {
  display: none;

  button {
    font-size: 0.6875rem;
  }
}
.event-content {
  padding-left: 48px;
}
.event-comment-container {
  padding-bottom: 21px;
  word-break: break-word;
  position: initial;

  &:hover {
    position: relative;

    .comment-edit-menu {
      display: initial;
    }
  }

  /deep/ h3 {
    font-size: 1.3125rem;
  }
}

/deep/ .event-header .user-name {
  font-weight: 600;
}

.event-time {
  color: $secondary;
  line-height: 1.375rem;
}

.comment-save-menu {
  padding-left: 34px;
  bottom: 1px;

  button {
    font-size: 0.875rem;
  }
}

.event-comment {
  white-space: pre-wrap;
}

.edit-comment {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: $quinary;
  border: 1px solid $borderPrimary;
  border-radius: 14px;
  padding: 0 16px 16px 16px;
  margin: 8px 0 16px 0;
  min-height: 314px;

  &.errored-field {
    border: 1px solid $error;
  }
}

.actor-avatar {
  align-items: baseline;
  margin-top: 1px;
}

@include media-breakpoint-down(md) {
  .comment-edit-menu {
    display: initial;

    button {
      text-decoration: underline;
    }
  }
}
</style>
